import * as is from 'is';
import { ValueError } from '@utils/errors';
import { clamp } from '@utils/numberUtils';

export class CSVProcessor {
  #headerRow;
  #additionalHeaderColumns;
  #additionalHeaderColumnNames;
  #rows;
  #content = [];

  constructor() {
    this.initialize();
  }

  /**
   * Property.
   *
   * Returns a list of header columns.
   *
   * @returns {string[]}
   */
  get headerRow() {
    const headerRow = [].concat(...this.#headerRow);
    for (const { name, atIndex } of this.#additionalHeaderColumns) {
      headerRow.splice(atIndex, 0, name);
    }
    return headerRow;
  }

  /**
   * Property.
   *
   * Returns a list of row objects.
   *
   * @returns {object[]}
   */
  get rows() {
    return [].concat(...this.#rows);
  }

  get numRows() {
    return this.#rows.length;
  }

  *[Symbol.iterator]() {
    let index = 0;
    while (index < this.#rows.length) {
      yield this.#rows[index];
      index++;
    }
  }

  contents() {
    if (is.empty(this.#content)) {
      return false;
    }
    const [header, rows] = this.#content.shift();
    this.initialize();
    this.setHeaderRow(header);
    for (const row of rows) {
      this.addRow(row);
    }
    return true;
  }

  /**
   * Initializes the rows and additional header columns lists.
   */
  initialize() {
    this.#rows = [];
    this.#additionalHeaderColumns = [];
    this.#additionalHeaderColumnNames = new Set();
  }

  process(contents) {
    if (!is.string(contents)) {
      contents = contents.toString();
    }
    const [header, ...rows] = contents.split(/(?:\r\n|\r\n)/gm);
    this.setHeaderRow(header);
    for (const row of rows) {
      this.addRow(row);
    }
  }

  isValid(headers) {
    // make certain that the required headers are present in the header row.
    const difference = headers.filter((header) => !this.#headerRow.includes(header));
    return difference.length === 0;
  }

  /**
   * Sets the header row for the document.
   *
   * @param {string|string[]} stringOrStringArray Either a delimited string or a list of strings representing
   * CSV header column names.
   * @param {string} [delimiter = ','] A character, or charaters, used to separate the header column names if
   * the stringOrStringArray is a string.
   * @throws {ValueError} If the value for stringOrStringArray argument is empty.
   * @throws {TypeError} If the stringOrStringArray argument is neither a string or list of non-empty strings.
   * @throws {ValueError} If a column name is repeated in the stringOrStringArray argument.
   * @returns {void}
   */
  setHeaderRow(stringOrStringArray, delimiter = ',') {
    if (is.string(stringOrStringArray)) {
      stringOrStringArray = stringOrStringArray.split(delimiter);
    }
    if (is.array(stringOrStringArray)) {
      if (is.empty(stringOrStringArray)) {
        throw new ValueError('The argument passed to setHeaderRow cannot be empty.');
      }
      const headerColumns = [].concat(...stringOrStringArray);
      while (!is.empty(headerColumns)) {
        const column = headerColumns.pop();
        if (headerColumns.includes(column)) {
          throw new ValueError(`Column "${column}" is repeated in the header.`);
        }
      }
      this.#headerRow = stringOrStringArray;
      return;
    }
    throw new TypeError('The first argument passed to setHeaderRow was neither a string or array.');
  }
  /**
   * Adds a row.
   *
   * @param {string|string[]} stringOrStringArray Either a delimited string or a list of strings representing
   * CSV header column names.
   * @param {*} [delimiter = ','] A character, or charaters, used to separate the header column names if
   * the stringOrStringArray is a string.
   * @throws {ValueError} When a header row has not yet been set.
   * @throws {ValueError} When the row length is not equal to the header row length.
   * @throws {ValueError} When the first argument is neither a string or non-empty string list.
   * @returns {void}
   */
  addRow(stringOrStringArray, delimiter = ',') {
    if (!is.array(this.#headerRow)) {
      throw new ValueError('No header row has been set. Please set the header row before trying to add rows.');
    }
    if (is.string(stringOrStringArray)) {
      if (is.empty(stringOrStringArray)) {
        return;
      }
      stringOrStringArray = stringOrStringArray.split(delimiter);
    }

    if (is.array(stringOrStringArray)) {
      if (this.#headerRow.length !== stringOrStringArray.length) {
        const message = [
          'Unable to process the file.',
          'It may not by a valid CSV or has mismatch header and row columns.',
          'Please use the provided template and upload it as a CSV.',
        ].join(' ');
        throw new ValueError(message);
      }
      const rowObject = {};
      for (const [index, columnName] of Object.entries(this.#headerRow)) {
        rowObject[columnName] = stringOrStringArray[index];
      }
      this.#rows.push(rowObject);
      return;
    }
    throw new TypeError('The first argument passed to setHeaderRow was neither a string or array.');
  }
  /**
   * Adds a header column at a specified index of the header row.
   *
   * @param {string} name The name of the column to add.
   * @param {number} [atIndex = 0] The index point in the header row to add the column. Defaults to 0.
   * @param {boolean} [errorGracefully = true] Indicates if the method should not throw errors. Defaults to true.
   * @throws {ValueError} If the name for the column already exists and errorGracefully is false.
   * @throws {ValueError} If the name for the column is already set to be created and errorGracefully is false.
   * @returns {void}
   */
  addHeaderColumn(name, atIndex = 0, errorGracefully = true) {
    if (this.#headerRow.includes(name)) {
      if (errorGracefully) {
        return;
      }
      throw new ValueError(`A column with the name "${name}" already exists.`);
    }
    if (this.#additionalHeaderColumnNames.has(name)) {
      if (errorGracefully) {
        return;
      }
      throw new ValueError(`A column with the name "${name}" is already set to be created.`);
    }
    atIndex = clamp(atIndex, { minimum: 0, maximum: this.#headerRow.length });
    this.#additionalHeaderColumns.push({ name, atIndex });
    this.#additionalHeaderColumnNames.add(name);
  }
  /**
   * Returns a list of the rows, including the header row, as strings separated by the provided delimiter.
   *
   * @param {string} [delimiter = ','] A character, or charaters, used to separate the header column names if
   * the stringOrStringArray is a string. Defaults to comma (,).
   * @returns {string[]}
   */
  asStringRows(delimiter = ',') {
    const headerRow = this.headerRow;
    const rows = [headerRow.join(',').trim()];
    for (const row of this.#rows) {
      const _row = [];
      for (const column of headerRow) {
        _row.push(row[column] ?? '');
      }
      rows.push(_row.join(delimiter).trim());
    }
    return rows;
  }
  /**
   * Returns the rows as a single CRLF delimited string.
   *
   * @param {string} [delimiter = ','] A character, or charaters, used to separate the header column names if
   * the stringOrStringArray is a string. Defaults to comma (,).
   * @returns {string}
   */
  asStringDocument(delimiter = ',') {
    return this.asStringRows(delimiter).join('\r\n').trim();
  }
}
